import React, { useEffect, useState } from 'react';
import PaymentsIcon from '@mui/icons-material/Payments';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { IconButton, Badge, MenuItem, Menu, Typography, Box, Divider, Button, Stack } from '@mui/material';
import { useSelector, useStore } from 'react-redux';
import { styled } from '@mui/material/styles';

const CartMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  width: '280px',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.error.main,
  padding: 4,
  '&:hover': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
}));

interface PaymentsCartProps {
  history: {
    push: (path: string) => void;
  };
}

export default function PaymentsCart(props: PaymentsCartProps) {
  const store = useStore();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [animate, setAnimate] = useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  interface RootState {
    payment: {
      parcelIDs: number[];
    };
  }

  const parcelIDsLength = useSelector((state: RootState) => {
    return state?.payment.parcelIDs.length;
  });

  const paymentParcels = useSelector((state: RootState) => {
    return state?.payment.parcelIDs;
  });

  useEffect(() => {
    setAnimate(true);
    setTimeout(() => {
      setAnimate(false);
    }, 200);
  }, [parcelIDsLength]);

  return (
    <div>
      <IconButton
        aria-label="payment cart"
        aria-controls="payment-menu"
        aria-haspopup="true"
        onClick={handleMenu}
        color="inherit"
        style={{
          transform: animate ? 'scale(1.2)' : 'scale(1)',
          transition: 'transform 0.2s',
        }}
      >
        <Badge
          badgeContent={parcelIDsLength}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.75rem',
              fontWeight: 'bold',
            },
          }}
        >
          <PaymentsIcon />
        </Badge>
      </IconButton>

      <Menu
        id="payment-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            borderRadius: 1,
            width: 300,
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Payment Cart
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {parcelIDsLength} {parcelIDsLength === 1 ? 'item' : 'items'} selected
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ maxHeight: '40vh', overflowY: 'auto' }}>
          {paymentParcels.length > 0 ? (
            paymentParcels.map((parcelID) => (
              <CartMenuItem key={parcelID}>
                <Typography variant="body1">Parcel ID: {parcelID}</Typography>
                <DeleteButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    store.dispatch({
                      type: 'REMOVE_PARCEL_ID',
                      payload: parcelID,
                    });
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </DeleteButton>
              </CartMenuItem>
            ))
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No parcels added to payment cart
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        <Stack direction="row" spacing={1} sx={{ p: 1.5, justifyContent: 'space-between' }}>
          <ActionButton
            variant="outlined"
            color="error"
            size="small"
            startIcon={<ClearAllIcon />}
            disabled={paymentParcels.length === 0}
            onClick={() => {
              store.dispatch({ type: 'CLEAR_PARCEL_IDS' });
              handleClose();
            }}
          >
            Clear All
          </ActionButton>

          <ActionButton
            variant="contained"
            color="primary"
            size="small"
            startIcon={<ShoppingCartCheckoutIcon />}
            disabled={paymentParcels.length === 0}
            onClick={() => {
              props.history.push('/dashboard/payment/create');
              handleClose();
            }}
          >
            Process Payment
          </ActionButton>
        </Stack>
      </Menu>
    </div>
  );
}
